/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { BenefitNameEnum } from "Constants/BenefitConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { IBenefit } from "Interfaces/Benefits/IBenefit";
import { IFilterValue } from "Interfaces/IFilterValue";

import {
  BENEFITS_FILTER_VALUE_ADD,
  BENEFITS_FILTER_VALUE_REMOVE,
  BENEFITS_FILTER_VALUE_UPDATE,
  BENEFITS_RESET,
  BENEFITS_SEARCH,
  BENEFITS_SEARCH_FAIL,
  BENEFITS_SEARCH_SUCCESS,
  UPDATE_BENEFIT_LISTING_CURRENT_PAGE,
  UPDATE_BENEFIT_LISTING_PAGE_SIZE,
} from "./BenefitActionTypes";

/**
 * Action for when searching benefits.
 * @interface
 */
export interface ISearchBenefits {
  type: typeof BENEFITS_SEARCH;
  tenant: TenantTypesEnum;
  benefitName: BenefitNameEnum;
  searchText: string;
  selectedBenefitsFilterValues: IFilterValue[];
  searchPage: number;
  pageSize: number;
}

/**
 * Action for when benefits were searched successfully.
 * @interface
 */
export interface ISearchBenefitsSuccess {
  type: typeof BENEFITS_SEARCH_SUCCESS;
  benefits: IBenefit[];
  searchPage: number;
  pageSize: number;
  filteredCount: number;
  unclaimedCount: number;
}

/**
 * Action for when loading benefits failed.
 * @interface
 */
export interface ISearchBenefitsFail {
  type: typeof BENEFITS_SEARCH_FAIL;
  error: string;
}

export interface IResetBenefits {
  type: typeof BENEFITS_RESET;
}

/**
 * Action for when updating listing current page
 * @interface
 */
export interface IUpdateCurrentPage {
  type: typeof UPDATE_BENEFIT_LISTING_CURRENT_PAGE;
  currentPage: number;
}

/**
 * Action for when updating listing page size
 * @interface
 */
export interface IUpdatePageSize {
  type: typeof UPDATE_BENEFIT_LISTING_PAGE_SIZE;
  pageSize: number;
}

/**
 * Action for when adding selected filter value.
 * @interface
 */
export interface IBenefitsFilterValueAdd {
  type: typeof BENEFITS_FILTER_VALUE_ADD;
  filterValue: IFilterValue;
}

/**
 * Action for when removing selected filter value.
 * @interface
 */
export interface IBenefitsFilterValueRemove {
  type: typeof BENEFITS_FILTER_VALUE_REMOVE;
  filterValue: IFilterValue;
}

/**
 * Action for when updating selected filter value.
 * @interface
 */
export interface IBenefitsFilterValueUpdate {
  type: typeof BENEFITS_FILTER_VALUE_UPDATE;
  filterValue: IFilterValue;
}

/**
 * Action creator for BENEFITS_SEARCH.
 */
export function searchBenefits(
  tenant: TenantTypesEnum,
  benefitName: BenefitNameEnum,
  searchText: string,
  selectedBenefitsFilterValues: IFilterValue[],
  searchPage: number,
  pageSize: number
): BenefitActionTypes {
  return {
    type: BENEFITS_SEARCH,
    tenant,
    benefitName,
    searchText,
    selectedBenefitsFilterValues,
    searchPage,
    pageSize,
  };
}

/**
 * Action creator for BENEFITS_SEARCH_SUCCESS.
 * @param benefits the user profiles successfully searched.
 */
export function searchBenefitsSuccess(
  benefits: IBenefit[],
  searchPage: number,
  pageSize: number,
  filteredCount: number,
  unclaimedCount: number
): BenefitActionTypes {
  return {
    type: BENEFITS_SEARCH_SUCCESS,
    benefits,
    searchPage,
    pageSize,
    filteredCount,
    unclaimedCount,
  };
}

/**
 * Action creator for BENEFITS_SEARCH_FAIL.
 * @param error the error message from the API.
 */
export function searchBenefitsFail(error: string): BenefitActionTypes {
  return {
    type: BENEFITS_SEARCH_FAIL,
    error,
  };
}

export function resetBenefits(): IResetBenefits {
  return {
    type: BENEFITS_RESET,
  };
}

/**
 * Action creator for UPDATE_BENEFIT_LISTING_CURRENT_PAGE.
 */
export function updateCurrentPage(currentPage: number): BenefitActionTypes {
  return {
    type: UPDATE_BENEFIT_LISTING_CURRENT_PAGE,
    currentPage,
  };
}

/**
 * Action creator for UPDATE_BENEFIT_LISTING_PAGE_SIZE.
 */
export function updatePageSize(pageSize: number): BenefitActionTypes {
  return {
    type: UPDATE_BENEFIT_LISTING_PAGE_SIZE,
    pageSize,
  };
}

/**
 * Action creator for BENEFITS_FILTER_VALUE_ADD.
 * @param error the error message from the API.
 */
export function addBenefitsFilterValue(filterValue: IFilterValue): BenefitActionTypes {
  return {
    type: BENEFITS_FILTER_VALUE_ADD,
    filterValue,
  };
}

/**
 * Action creator for BENEFITS_FILTER_VALUE_REMOVE.
 * @param error the error message from the API.
 */
export function removeBenefitsFilterValue(filterValue: IFilterValue): BenefitActionTypes {
  return {
    type: BENEFITS_FILTER_VALUE_REMOVE,
    filterValue,
  };
}

/**
 * Action creator for BENEFITS_FILTER_VALUE_UPDATE.
 * @param error the error message from the API.
 */
export function updateBenefitsFilterValue(filterValue: IFilterValue): BenefitActionTypes {
  return {
    type: BENEFITS_FILTER_VALUE_UPDATE,
    filterValue,
  };
}

export type BenefitActionTypes =
  | ISearchBenefits
  | ISearchBenefitsSuccess
  | ISearchBenefitsFail
  | IResetBenefits
  | IUpdateCurrentPage
  | IUpdatePageSize
  | IBenefitsFilterValueAdd
  | IBenefitsFilterValueRemove
  | IBenefitsFilterValueUpdate;
